import type { CSSProperties, ReactNode } from "react";

/**
 * Visual tone for a state pill.
 *
 * Mapping rules for state-machine pages:
 *   - `neutral` — loading, empty, not-entitled, feature-unavailable
 *   - `info`    — informational notices that aren't blocking
 *   - `success` — positive confirmation chips
 *   - `warn`    — degraded / forbidden / soft errors that don't break the surface
 *   - `danger`  — hard errors / failed operations
 *
 * Pages compose `StatePill` directly when the state is inline (e.g. a
 * dashboard tile that lives inside a Card already), and wrap it in a
 * `<Card>` themselves when the state owns a full content row (e.g.
 * the Insights page's full-page "feature unavailable" message).
 */
export type StatePillTone = "neutral" | "info" | "success" | "warn" | "danger";

export interface StatePillProps {
  label: string;
  tone?: StatePillTone;
  iconStart?: ReactNode;
  /**
   * ARIA role override. Defaults to `status` so screen readers
   * announce the pill as a polite live update. Use `alert` for
   * blocking errors; pass `undefined` to opt out entirely.
   */
  role?: "status" | "alert" | "note";
  style?: CSSProperties;
  /**
   * Sprint 33 (GT-TENANT-ADMIN-COMPLETE-02) — additive `data-testid`
   * passthrough. Sprint 31 + Sprint 32 closeouts both flagged the
   * absence of `data-testid` forwarding as "Foundation friction"
   * (callers had to wrap the pill in `<span data-testid=…>` just
   * to assert on it in tests). Adding it as an optional prop is
   * fully additive — existing callers see no behaviour change.
   */
  dataTestId?: string;
}

const toneColor: Record<StatePillTone, string> = {
  neutral: "var(--gt-sys-text-muted)",
  info: "var(--gt-sys-info, var(--gt-sys-text-muted))",
  success: "var(--gt-sys-success, var(--gt-sys-text-muted))",
  warn: "var(--gt-sys-warning, var(--gt-sys-text-muted))",
  danger: "var(--gt-sys-danger, var(--gt-sys-text-muted))",
};

export function StatePill({
  label,
  tone = "neutral",
  iconStart,
  role = "status",
  style,
  dataTestId,
}: StatePillProps) {
  const computed: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--gt-sys-space-1)",
    color: toneColor[tone],
    // Sprint 16 Wave G — standardize on the typography token instead
    // of a hard-coded 13. Pairs with --gt-sys-font-size-sm (= 13px in
    // the default scale; lets a future operator override the scale
    // without forking the StatePill).
    fontSize: "var(--gt-sys-font-size-sm)",
    background: "var(--gt-sys-surface-muted, var(--gt-sys-bg-subtle))",
    paddingBlock: "var(--gt-sys-space-1)",
    paddingInline: "var(--gt-sys-space-2)",
    borderRadius: 999,
    alignSelf: "flex-start",
    ...style,
  };
  return (
    <span role={role} style={computed} {...(dataTestId ? { "data-testid": dataTestId } : {})}>
      {iconStart ? <span aria-hidden="true">{iconStart}</span> : null}
      <span>{label}</span>
    </span>
  );
}
